/**
 * @voltxampere/shared — Levenshtein distance helper (Sprint 9)
 *
 * Straightforward dynamic-programming implementation. Used by
 * the assertion evaluator to generate "Did you mean X?" hints
 * when a user references a non-existent node or component.
 *
 * Complexity: O(m·n) time, O(min(m, n)) space using the classic
 * two-row rolling DP.
 *
 * Sprint 9 contract:
 *
 *   - `levenshtein("VOUT", "V_OUT") === 1` (one insertion)
 *   - `levenshtein("q1", "Q1") === 2` (case-sensitive by default)
 *   - `levenshtein("", "abc") === 3`
 *   - `levenshtein("abc", "abc") === 0`
 *
 * A `caseSensitive: false` option is provided because assertion
 * identifiers are case-sensitive in the circuit schema but the
 * "did you mean" UX often wants to surface `vout` when the user
 * types `VOUT`. The suggestion picker tolerates either.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
/**
 * Edit distance between two strings. Insertion, deletion, and
 * substitution all cost 1.
 */
export function levenshtein(a, b, options = {}) {
    const caseSensitive = options.caseSensitive ?? true;
    const x = caseSensitive ? a : a.toLowerCase();
    const y = caseSensitive ? b : b.toLowerCase();
    if (x === y)
        return 0;
    if (x.length === 0)
        return y.length;
    if (y.length === 0)
        return x.length;
    // Ensure x is the shorter string to minimise the rolling row.
    const [shorter, longer] = x.length <= y.length ? [x, y] : [y, x];
    const prev = new Array(shorter.length + 1);
    const next = new Array(shorter.length + 1);
    for (let i = 0; i <= shorter.length; i++)
        prev[i] = i;
    for (let i = 1; i <= longer.length; i++) {
        next[0] = i;
        for (let j = 1; j <= shorter.length; j++) {
            const cost = longer[i - 1] === shorter[j - 1] ? 0 : 1;
            const del = (prev[j] ?? 0) + 1;
            const ins = (next[j - 1] ?? 0) + 1;
            const sub = (prev[j - 1] ?? 0) + cost;
            next[j] = Math.min(del, ins, sub);
        }
        for (let j = 0; j <= shorter.length; j++)
            prev[j] = next[j] ?? 0;
    }
    return prev[shorter.length] ?? 0;
}
/**
 * Given a target string and a pool of candidates, return the
 * closest candidate within the given `maxDistance` (default 3).
 * Returns `null` when nothing is close enough. Ties resolve to
 * the first candidate in the pool — callers order the pool by
 * preference (shortest, most common, alphabetical…) before
 * calling.
 */
export function suggestClosest(target, pool, maxDistance = 3, options = {}) {
    if (pool.length === 0)
        return null;
    let best = null;
    let bestDist = maxDistance + 1;
    for (const candidate of pool) {
        const d = levenshtein(target, candidate, options);
        if (d < bestDist) {
            best = candidate;
            bestDist = d;
            if (d === 0)
                return candidate;
        }
    }
    return bestDist <= maxDistance ? best : null;
}
//# sourceMappingURL=levenshtein.js.map